import {
  Button,
  CardContent,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { getChallenge, submitEntry } from 'actions/challenge';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import './index.css';
type SubmitFormProps = {
  challengeId: string;
  title?: string;
  submissionRedirect: string;
};
const SubmitForm = ({
  challengeId,
  title = 'Submit',
  submissionRedirect,
}: SubmitFormProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const action = (snackbarId: string) => (
    <>
      <Button
        color="inherit"
        onClick={() => {
          closeSnackbar(snackbarId);
        }}
      >
        Dismiss
      </Button>
    </>
  );
  const history = useHistory();
  const [inSubmission, setInSubmission] = useState<boolean>(false);
  const [challenge, setChallenge] = useState<any>();
  const [tags, setTags] = useState<string[]>(["Imitation/Reinforcement Learning (Rigid Body)"]);

  const ALL_TASKS = [
    "PickCube",
    "AssemblingKits",
    "PickSingleEGAD",
    "PandaAvoidObstacles",
    "PegInsertionSide",
    "PickSingleYCB",
    "StackCube",
    "PlugCharger",
    "TurnFaucet",
    "PickClutter",
    // # "ServeFood", # not included for now

    "OpenCabinetDrawer",
    "OpenCabinetDoor",
    "PushChair",
    "MoveBucket",

    "Fill",
    "Excavate",
    "Pour",
    "Hang",
    "Write",
    "Pinch",
  ];
  const SOFT_BODY_TASKS = new Set(['Fill', 'Excavate', 'Pour', 'Hang', 'Write', 'Pinch'])
  const RIGID_BODY_TASKS = new Set(ALL_TASKS);
  SOFT_BODY_TASKS.forEach((v) => {
    RIGID_BODY_TASKS.delete(v);
  });
  const [tasks, setTasks] = useState<Set<string>>(RIGID_BODY_TASKS);
  useEffect(() => {
    getChallenge(challengeId).then((c) => {
      setChallenge(c);
    });
  }, [challengeId]);
  const handleSelectChange = (e: any) => {
    // set default tasks as well
    if (e.target.value.match("Rigid Body")) {
      setTasks(RIGID_BODY_TASKS);
    } else {
      setTasks(SOFT_BODY_TASKS);
    }
    setTags([e.target.value]);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    setInSubmission(true);

    e.preventDefault();
    let data = new FormData(e.target as HTMLFormElement);
    let docker = data.get('docker')!.toString();
    // check formatting of docker image and also look for an image tag
    const colon_index = docker.indexOf(":");
    if (colon_index === -1) {
      enqueueSnackbar('Missing docker tag in docker image. Docker image format should be <registry_url>/<username>/<image>:<tag>', {
        variant: 'error',
        action,
        autoHideDuration: 10000,
      });
      setInSubmission(false);
      return;
    }
    const entry = {
      docker,
      tasks: Array.from(tasks),
    };
    if (entry.tasks.length === 0) {
      enqueueSnackbar('Need to select at least one task for evaluation', {
        variant: 'error',
        action
      });
      setInSubmission(false);
      return;
    }
    let desc = data.get('description')!.toString();
    let name = data.get('name')!.toString();

    const uploadSnackbarId = enqueueSnackbar('Uploading...', {
      variant: 'info',
      persist: false,
      action,
    });
    submitEntry(challengeId, name, entry, desc, tags)
      .then(() => {
        enqueueSnackbar('Submitted successfully', {
          variant: 'success',
          action,
        });
        history.push(submissionRedirect);
      })
      .catch((err) => {
        if (err.response.data && err.response.data.message) {
          enqueueSnackbar(err.response.data.message, {
            variant: 'error',
            action,
          });
        } else {
          enqueueSnackbar(err.message, {
            variant: 'error',
            action
          });
        }
      })
      .finally(() => {
        closeSnackbar(uploadSnackbarId);
        setInSubmission(false);
      });
  };
  const handleTaskCheck = (e: any) => {
    const newTasks: Set<string> = new Set();
    tasks.forEach((task) => newTasks.add(task));
    if (e.target.checked) {
      newTasks.add(e.target.value);
    } else {
      newTasks.delete(e.target.value);
    }
    setTasks(newTasks);
  };
  if (challenge === undefined) return <></>;
  if (challenge.status === 'closed')
    return <div>The submission has closed</div>;

  return (
    <div className="SubmitForm">
      <Paper elevation={3} className="submit-card">
        <CardContent>
          <Typography variant="h5" style={{ fontWeight: 700 }} sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2">Make sure to read <a href="https://github.com/haosulab/ManiSkill2/wiki/Participation-Guidelines" target="_blank" rel="noreferrer">participation guidelines</a> first before submitting</Typography>
          <br/>
          <form onSubmit={handleSubmit}>
            <TextField
              className="name-input"
              label="Entry name (visible to everyone)"
              color="primary"
              name="name"
              type="text"
              required
            />
            <TextField
              className="docker-input"
              label="Docker Image"
              color="primary"
              name="docker"
              type="text"
              multiline
              required
            />
            <TextField
              className="description-input"
              label="Entry description (visible to everyone)"
              color="primary"
              name="description"
              type="text"
              multiline
            />
            <br />
            <InputLabel id="track-select-label">
              <Typography variant="body2">Track *</Typography>
            </InputLabel>
            <Select
              labelId="track-select-label"
              className="track-select"
              value={tags.length > 0 ? tags[0] : ''}
              onChange={handleSelectChange}
              required
            >
              <MenuItem value={'Imitation/Reinforcement Learning (Rigid Body)'}>
                Imitation/Reinforcement Learning (Rigid Body)
              </MenuItem>
              <MenuItem value={'No Restriction (Rigid Body)'}>
                No Restriction (Rigid Body)
              </MenuItem>
              <MenuItem value={'No Restriction (Soft Body)'}>
                No Restriction (Soft Body)
              </MenuItem>
            </Select>
            <br />
            <br />
            <div className="tasks-boxes">
              <InputLabel>
                <Typography variant="body2">Tasks to Run</Typography>
                <Button
                  variant="contained"
                  onClick={() => setTasks(new Set(ALL_TASKS))}
                >
                  Tick All
                </Button>{' '}
                <Button variant="contained" onClick={() => setTasks(new Set())}>
                  Untick All
                </Button>
              </InputLabel>
              {ALL_TASKS.filter((task_id) =>{
                if (tags[0].match("Rigid Body")) {
                  return !SOFT_BODY_TASKS.has(task_id)
                }
                return SOFT_BODY_TASKS.has(task_id)
              }).map((task_id) => {
                return (
                  <FormControlLabel
                    control={
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: 12 }}
                      >
                        <Checkbox
                          size="small"
                          value={task_id}
                          checked={tasks.has(task_id)}
                          onClick={handleTaskCheck}
                        />
                      </Typography>
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                        {task_id}
                      </Typography>
                    }
                  />
                );
              })}
            </div>
            <p>
              Make sure the <code>mani_skill2</code> package is <b>excluded</b>{' '}
              from your submission!
            </p>
            <p>Note that you cannot resubmit a previously submitted docker image and tag. In order to ensure reproducibility we require you to uniquely tag every submitted docker image</p>
            <p>If you are planning to create a final submission, make sure to tick all boxes to ensure your submission works on all tasks.</p>
            <Button
              className="submit-btn"
              type="submit"
              variant="contained"
              disabled={inSubmission}
            >
              {!inSubmission ? (
                'Upload'
              ) : (
                <>
                  <span style={{ marginRight: '0.5rem' }}>Uploading</span>
                  <CircularProgress
                    style={{ verticalAlign: 'middle' }}
                    size={20}
                  />
                </>
              )}
            </Button>
            {/* vertical-align: middle; */}
          </form>
        </CardContent>
      </Paper>
    </div>
  );
};

export default SubmitForm;
